/**
 * Registration/Re-Registration student list API calls with React Query.
 * Shared by the Student Admin>Registration pages and the Registration Dropdown on the Dashboard
 * Registration widget so both hit the same query cache for equal requestData.
 */
import { GetAllNewRegistrationsStudentsList, GetAllReRegistrationsStudentsList, GetAllRegistrationsStudentsList, GetAllSLPRegistrationsStudentsList, GetStudentListBasedOnReregistrationCancelToken } from "../../services/RegistrationService";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";

const EMPTY_REGISTRATIONS_LIST = Object.freeze({
    studentsList: [],
    totalRows: 0
});

const EMPTY_PROGRAMME_REREGISTRATION_STUDENT_LIST = Object.freeze({
    studentsList: [],
    totalRows: 0
});

/**
 * Builds a React Query hook for one of the registration list endpoints.
 * All 4 endpoints return the same `registrations_student_list.{data,total}` envelope.
 *
 * @param {string} subKey QUERY_KEYS.registration sub-key identifying this list.
 * @param {Function} listServiceFn Service function accepting requestData and returning the axios response.
 * @returns {Function} React Query hook accepting requestData.
 */
function createRegistrationListHook(subKey, listServiceFn) {
    return function useRegistrationList(requestData) {
        const query = useBaseReactQuery(
            [QUERY_KEYS.registration.root, subKey, requestData],
            async () => {
                const { data } = await listServiceFn(requestData);

                return {
                    studentsList: data?.registrations_student_list?.data ?? [],
                    totalRows: data?.registrations_student_list?.total ?? 0
                };
            },
            {
                cacheProfile: QUERY_CACHE_PROFILES.TABLE
            }
        );

        return {
            ...query,
            data: query.data ?? EMPTY_REGISTRATIONS_LIST
        };
    };
}

/**
 * React Query hook for the "All Registrations" list.
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export const useAllRegistrationsList = createRegistrationListHook(QUERY_KEYS.registration.allRegistrationsList, GetAllRegistrationsStudentsList);

/**
 * React Query hook for the "Registrations" (new registrations only) list.
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export const useRegistrationsOnlyList = createRegistrationListHook(QUERY_KEYS.registration.registrationsList, GetAllNewRegistrationsStudentsList);

/**
 * React Query hook for the "Re-Registrations" list.
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export const useReRegistrationsList = createRegistrationListHook(QUERY_KEYS.registration.reRegistrationsList, GetAllReRegistrationsStudentsList);

/**
 * React Query hook for the "SLP Registrations" list.
 * @param {object} requestData Request body passed directly to the API.
 * @returns {DefinedUseQueryResult}
 */
export const useSLPRegistrationsList = createRegistrationListHook(QUERY_KEYS.registration.slpRegistrationsList, GetAllSLPRegistrationsStudentsList);

/**
 * React Query hook for the student list of one open Programme Re-Registration cycle.
 * Response envelope differs from the other registration lists (`qualication`/`pagination.total`
 * rather than `registrations_student_list`), so this isn't built on createRegistrationListHook.
 *
 * @param {object} requestData Request body passed directly to the API (includes the re-registration id, pagination, sort and filters).
 * @returns {DefinedUseQueryResult}
 */
export function useProgrammeReRegistrationStudentList(requestData) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.registration.root, QUERY_KEYS.registration.programmeReRegistrationStudentList, requestData],
        async () => {
            const { data } = await GetStudentListBasedOnReregistrationCancelToken(requestData);

            return {
                studentsList: data?.qualication ?? [],
                totalRows: data?.pagination?.total ?? 0
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_PROGRAMME_REREGISTRATION_STUDENT_LIST
    };
}
